import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { FormRow, Form, FormGroup, Help } from '@/components/Form'

import { Button } from '@/components/Button'
import styles from './AddCollaboratorsAsAdminDialog.module.css'
import { ErrorBlock } from '@/components/ErrorBlock'
import {
  RadioButtonGroup,
  RadioButtonItem,
} from '@/components/RadioButtonGroup'

export interface AddCollaboratorsAsAdminDialogProps {
  showing: boolean
  onClose: () => unknown
  onSubmit: () => unknown
  isSubmitPending: boolean
  isSubmitDisabled: boolean
  isSubmitRejected: boolean
  submitError?: unknown
  isTypeSelectorVisible: boolean
  isAddingClient: boolean
  isAddingPro: boolean
  onAddingClient: () => unknown
  onAddingPro: () => unknown
  children?: ReactNode
}

export function AddCollaboratorsAsAdminDialog({
  showing,
  onClose,
  onSubmit,
  isSubmitPending,
  isSubmitDisabled,
  isSubmitRejected,
  submitError,
  isTypeSelectorVisible,
  isAddingClient,
  isAddingPro,
  onAddingClient,
  onAddingPro,
  children,
}: AddCollaboratorsAsAdminDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <div className={styles.contentBlock}>
          <ModalSheetContentTitle align="left" compact>
            <span className={styles.title}>Add a collaborator</span>
          </ModalSheetContentTitle>
          <ModalSheetContentDescription align="left" compact>
            <span className={styles.description}>
              The collaborator will have access to the job
            </span>
          </ModalSheetContentDescription>
        </div>

        <ModalSheetContentBlock>
          {isTypeSelectorVisible && (
            <FormRow>
              <div className={styles.filterTitle}>
                What would you like to add?
              </div>
              <RadioButtonGroup size="big">
                <RadioButtonItem
                  onPress={onAddingClient}
                  active={isAddingClient}
                  testId="add-job-client"
                >
                  Client
                </RadioButtonItem>
                <RadioButtonItem
                  onPress={onAddingPro}
                  active={isAddingPro}
                  testId="add-job-pro"
                >
                  Pro
                </RadioButtonItem>
              </RadioButtonGroup>
            </FormRow>
          )}
          {children}
        </ModalSheetContentBlock>
      </ModalSheetContent>

      {isSubmitRejected && <ErrorBlock tackOn="bottom" error={submitError} />}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            isDisabled={isSubmitPending}
            onPress={onClose}
          />
          <Button
            label={'Add Collaborator'}
            variant="primary"
            isPending={isSubmitPending}
            testId="add-job-collaborator-as-admin-submit-button"
            onPress={onSubmit}
            isDisabled={isSubmitDisabled}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface AddCollaboratorFieldsProps {
  label: string
  select: ReactNode
  selectError?: string
  selectHelp?: ReactNode
  reasonField?: ReactNode
}

export function AddCollaboratorFields({
  label,
  select,
  selectError,
  selectHelp,
  reasonField,
}: AddCollaboratorFieldsProps) {
  return (
    <Form className={styles.row}>
      <FormGroup className={styles.selectCollaboratorContainer}>
        <div className={styles.addCollaboratorLabel}>{label}</div>
        {select}
        <Help error={selectError} help={selectHelp} />
      </FormGroup>
      {reasonField && (
        <FormRow className={styles.row}>
          <div className={styles.reasonLabel}>Reason</div>
          {reasonField}
        </FormRow>
      )}
    </Form>
  )
}

export interface AddCollaboratorProKindFilterProps {
  isQualifiedOnly: boolean
  isAnyAvailable: boolean
  onQualifiedOnly: () => unknown
  onAnyAvailable: () => unknown
}

export function AddCollaboratorProKindFilter({
  isQualifiedOnly,
  isAnyAvailable,
  onQualifiedOnly,
  onAnyAvailable,
}: AddCollaboratorProKindFilterProps) {
  return (
    <FormRow className={styles.row}>
      <div className={styles.filterTitle}>
        What kind of Pro are you looking for?
      </div>
      <RadioButtonGroup size="big">
        <RadioButtonItem
          onPress={onQualifiedOnly}
          active={isQualifiedOnly}
          testId="add-job-collaborator-select-qualified"
        >
          Qualified only
        </RadioButtonItem>
        <RadioButtonItem
          onPress={onAnyAvailable}
          active={isAnyAvailable}
          testId="add-job-collaborator-select-all"
        >
          Any available
        </RadioButtonItem>
      </RadioButtonGroup>
    </FormRow>
  )
}

export interface AddCollaboratorSearchItemDetailsProps {
  name?: string
  email?: string
}

export function AddCollaboratorSearchItemDetails({
  name,
  email,
}: AddCollaboratorSearchItemDetailsProps) {
  return (
    <>
      <div className={styles.collaboratorName}>{name}</div>
      <div className={styles.collaboratorEmail}>{email}</div>
    </>
  )
}
